import type { FC } from 'react';
import { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import { DropdownOption } from '@wix/da-ds/pkg/Dropdown/types';
import { changeCommissionStatus } from '@wix/da-shared-react/pkg/redux/commissions/actionCreators';
import { CommissionStatusType } from '@wix/da-shared-react/pkg/types/commissions';

import s from './CommissionStatusEdit.scss';

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

const CommissionStatusEdit: FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const inputId = useId();
  const options: DropdownOption[] = [
    {
      label: t('studio.products.commission.status.active'),
      value: CommissionStatusType.ACTIVE,
    },
    {
      label: t('studio.products.commission.status.disabled'),
      value: CommissionStatusType.DISABLED,
    },
  ];

  const dispatchChangeCommissionStatus = useAction(changeCommissionStatus);
  const onSelect = useCallback(
    (status: CommissionStatusType) => {
      dispatchChangeCommissionStatus({
        deviation,
        status,
      });
    },
    [dispatchChangeCommissionStatus, deviation]
  );

  return (
    <SelectField
      id={inputId}
      className={cn(s['root'], className)}
      label="Status"
      items={options}
      value={
        deviation.commission?.status === 1
          ? CommissionStatusType.ACTIVE
          : CommissionStatusType.DISABLED
      }
      onSelect={onSelect}
      isLabelHidden
      noBottomSpacing
      withBlankOption={false}
    />
  );
};
CommissionStatusEdit.displayName = 'CommissionStatusEdit';

export default CommissionStatusEdit;
